import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemText,
  Collapse,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Divider,
  TextField,
  Pagination,
} from '@mui/material';
import { api } from '../services/api';
import { auth } from '../services/auth';
import {
  getParentLatencyMs,
  getCapLatencyMs,
  getLatencyChipColor,
  formatLatencyMs,
  formatLatencyTooltip,
} from '../services/latency';
import DeviceMapView from './DeviceMapView';
import { devicePassesSignalFilter } from '../utils/backhaulRssiUtils';
import {
  devicePassesChildrenAllowedConfigFilter,
  devicePassesFiveGApConfigFilter,
  devicePassesSegmentFilter,
} from '../utils/backhaulMapFilterUtils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import RadioIcon from '@mui/icons-material/Radio';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { statusDotSx, healthChipSoftSx } from '../theme/mapViewUi';
import { formatDeviceLabel, getDeviceDisplayName } from '../utils/deviceDisplayUtils';

// Device type filtering is done client-side - no compatible filter needed for API calls



// New Radio View Component
const RadioView = ({ devices = [], isLoading, onDeviceClick }) => {
  const list = Array.isArray(devices) ? devices : [];

  const handleDeviceClick = (serial) => {
    onDeviceClick(serial);
  };

  if (list.length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Alert severity="info">No devices available.</Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Device Radios
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage radio interfaces for each device
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isLoading && (
            <CircularProgress size={20} />
          )}
        </Box>
      </Box>

      <Grid container spacing={2}>
        {list.map((device) => (
          <Grid item xs={12} md={6} lg={4} key={device.serial_number}>
            <Card 
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 6,
                },
                transition: 'box-shadow 0.3s ease-in-out'
              }}
              onClick={() => handleDeviceClick(device.serial_number.toLowerCase())}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      marginRight: 2,
                      ...statusDotSx(device.connected),
                    }}
                  />
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {formatDeviceLabel(device)}
                  </Typography>
                  <Chip
                    label={device.compatible}
                    size="small"
                    color={device.compatible === 'jnet2' ? 'primary' : 'default'}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={2}>
                  {/* Private WiFi Radios */}
                  {(device.private_wifi?.interfaces || device.private_wifi?.Interfaces)?.map((iface, index) => {
                    const radioInfo = device.radios?.find(r => r.name === iface.Radio?.Name);
                    return (
                      <Box 
                        key={`private-${index}`}
                        sx={{ 
                          p: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.paper'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <RadioIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                            {iface.Name || `Radio ${index + 1}`}
                          </Typography>
                          <Chip
                            label={iface.Mode}
                            size="small"
                            color={iface.Mode === 'station' ? 'warning' : 'primary'}
                          />
                        </Box>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Band</Typography>
                            <Typography variant="body2">{radioInfo?.band || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Channel</Typography>
                            <Typography variant="body2">{radioInfo?.channel || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Channel Width</Typography>
                            <Typography variant="body2">
                              {radioInfo?.channel_width ? `${radioInfo.channel_width}MHz` : 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">TX Power</Typography>
                            <Typography variant="body2">
                              {iface.TxPower ? `${iface.TxPower}dBm` : 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  })}

                  {/* Public WiFi Radios */}
                  {(device.public_wifi?.interfaces || device.public_wifi?.Interfaces)?.map((iface, index) => {
                    const radioInfo = device.radios?.find(r => r.name === iface.Radio?.Name);
                    return (
                      <Box 
                        key={`public-${index}`}
                        sx={{ 
                          p: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.paper'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <RadioIcon sx={{ mr: 1, color: 'info.main' }} />
                          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                            {iface.Name || `Radio ${index + 1}`}
                          </Typography>
                          <Chip
                            label={iface.Mode}
                            size="small"
                            color={iface.Mode === 'station' ? 'warning' : 'info'}
                          />
                        </Box>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Band</Typography>
                            <Typography variant="body2">{radioInfo?.band || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Channel</Typography>
                            <Typography variant="body2">{radioInfo?.channel || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Channel Width</Typography>
                            <Typography variant="body2">
                              {radioInfo?.channel_width ? `${radioInfo.channel_width}MHz` : 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">TX Power</Typography>
                            <Typography variant="body2">
                              {iface.TxPower ? `${iface.TxPower}dBm` : 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

/** Compact select styling shared by filter bar controls. */
const compactFilterFormSx = {
  minWidth: 72,
  flexShrink: 0,
  '& .MuiOutlinedInput-root': { height: 32 },
  '& .MuiSelect-select': {
    py: 0.375,
    px: 1,
    fontSize: '0.75rem',
    minHeight: 'unset !important',
  },
};

const filterLabelSx = {
  fontSize: '0.65rem',
  fontWeight: 600,
  color: 'text.secondary',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  lineHeight: 1.2,
};

/** Label on top, control below (+ optional help beside label). */
function FilterField({ label, helpTitle, helpLabel, children, sx }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flexShrink: 0, ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minHeight: 14 }}>
        <Typography component="span" sx={filterLabelSx}>
          {label}
        </Typography>
        {helpTitle ? (
          <FilterHelpTip title={helpTitle} label={helpLabel || `${label} help`} />
        ) : null}
      </Box>
      {children}
    </Box>
  );
}

/** Help icon beside a filter — tooltip only on the icon, not the select menu. */
function FilterHelpTip({ title, label = 'Filter help' }) {
  return (
    <Tooltip title={title} enterDelay={400} arrow placement="top">
      <IconButton
        size="small"
        aria-label={label}
        sx={{ p: 0, color: 'text.secondary', flexShrink: 0, width: 16, height: 16 }}
      >
        <HelpOutlineIcon sx={{ fontSize: 13 }} />
      </IconButton>
    </Tooltip>
  );
}

function DeviceList({ devices: devicesProp, devicesLoaded = false, onDevicesUpdate, onDeviceSelect, selectedDevice, onRefresh, highlightedGroupSerials }) {
  // Guard against null/undefined devices
  const devices = useMemo(() => (Array.isArray(devicesProp) ? devicesProp : []), [devicesProp]);
  // eslint-disable-next-line no-unused-vars
  const safeDevices = devices; // Legacy variable, kept for compatibility
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize filter state from URL parameters
  const getInitialFilterState = () => {
    const searchParams = new URLSearchParams(location.search);
    
    // Parse expanded devices from URL (comma-separated list of serial numbers)
    const expandedParam = searchParams.get('expanded');
    const expandedCaps = {};
    if (expandedParam) {
      expandedParam.split(',').forEach(serial => {
        if (serial.trim()) {
          expandedCaps[serial.trim()] = true;
        }
      });
    }
    
    const signalFilter = searchParams.get('signalFilter') || 'all';
    const fiveGApFilter = searchParams.get('fiveGApFilter') || 'all';
    const childrenAllowedFilter = searchParams.get('childrenAllowedFilter') || 'all';
    const segmentFilter = searchParams.get('segmentFilter') || 'all';
    return {
      viewMode: searchParams.get('viewMode') || 'map',
      connectionFilter: searchParams.get('connectionFilter') || 'all',
      deviceTypeFilter: searchParams.get('deviceTypeFilter') || 'network',
      signalFilter: ['all', 'ap', 'sta', 'both'].includes(signalFilter) ? signalFilter : 'all',
      fiveGApFilter: ['all', 'yes', 'no'].includes(fiveGApFilter) ? fiveGApFilter : 'all',
      childrenAllowedFilter: ['all', 'yes', 'no'].includes(childrenAllowedFilter) ? childrenAllowedFilter : 'all',
      segmentFilter,
      signalMinRssi: searchParams.get('signalMinRssi') || '',
      searchQuery: searchParams.get('searchQuery') || '',
      currentPage: parseInt(searchParams.get('currentPage')) || 1,
      itemsPerPage: parseInt(searchParams.get('itemsPerPage')) || 10,
      expandedCaps: expandedCaps
    };
  };

  const initialState = getInitialFilterState();
  
  const [viewMode, setViewMode] = useState(initialState.viewMode);
  const [expandedCaps, setExpandedCaps] = useState(initialState.expandedCaps);
  const [connectionFilter, setConnectionFilter] = useState(initialState.connectionFilter);
  const [deviceTypeFilter, setDeviceTypeFilter] = useState(initialState.deviceTypeFilter);
  const [signalFilter, setSignalFilter] = useState(initialState.signalFilter);
  const [fiveGApFilter, setFiveGApFilter] = useState(initialState.fiveGApFilter);
  const [childrenAllowedFilter, setChildrenAllowedFilter] = useState(initialState.childrenAllowedFilter);
  const [segmentFilter, setSegmentFilter] = useState(initialState.segmentFilter);
  const [customTrees, setCustomTrees] = useState([]);
  const [customTreesLoading, setCustomTreesLoading] = useState(false);
  const [signalMinRssi, setSignalMinRssi] = useState(initialState.signalMinRssi);
  const [searchQuery, setSearchQuery] = useState(initialState.searchQuery);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const canWriteBackhaul = auth.canWriteBackhaul();

  // If the list is empty but considered loaded (e.g., cache expired while on details page),
  // trigger a one-time refresh to repopulate.
  const emptyFetchAttemptedRef = useRef(false);
  useEffect(() => {
    if (!onRefresh) return;
    if (!devicesLoaded) return;
    const empty = !devices || devices.length === 0;
    if (!empty) {
      emptyFetchAttemptedRef.current = false; // reset when data is present
      return;
    }
    if (emptyFetchAttemptedRef.current) return;
    emptyFetchAttemptedRef.current = true;
    onRefresh({ force: true });
  }, [devices, devicesLoaded, onRefresh]);

  // Load custom-tree segments for the map segment filter.
  useEffect(() => {
    if (viewMode !== 'map') return undefined;
    let cancelled = false;
    setCustomTreesLoading(true);
    api.getCustomTrees()
      .then((response) => {
        if (cancelled) return;
        setCustomTrees(response?.trees || []);
      })
      .catch(() => {
        if (!cancelled) setCustomTrees([]);
      })
      .finally(() => {
        if (!cancelled) setCustomTreesLoading(false);
      });
    return () => { cancelled = true; };
  }, [viewMode]);

  // Drop stale segmentFilter from URL when the tree no longer exists.
  useEffect(() => {
    if (segmentFilter === 'all' || customTreesLoading) return;
    if (!customTrees.some((t) => t.id === segmentFilter)) {
      setSegmentFilter('all');
    }
  }, [customTrees, customTreesLoading, segmentFilter]);
  
  // No additional fetches needed - App.js fetches ALL devices with wifi_interfaces
  // All device type filtering is done client-side from the full list

  // Pagination state
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialState.itemsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [connectionFilter, deviceTypeFilter, signalFilter, fiveGApFilter, childrenAllowedFilter, segmentFilter, signalMinRssi, searchQuery]);

  // Update URL when filter state changes
  // CRITICAL: Only navigate if we're actually on the Devices tab route (/)
  // This prevents forcing navigation to / when user is on other tabs
  useEffect(() => {
    // Only update URL if we're on the Devices tab route
    // DeviceList is rendered for activeTab === 0, which corresponds to path === '/'
    if (location.pathname !== '/') {
      // User is on a different tab, don't navigate
      return;
    }
    
    const searchParams = new URLSearchParams();
    if (viewMode !== 'map') searchParams.set('viewMode', viewMode);
    if (connectionFilter !== 'all') searchParams.set('connectionFilter', connectionFilter);
    if (deviceTypeFilter !== 'network') searchParams.set('deviceTypeFilter', deviceTypeFilter);
    if (viewMode === 'map' && signalFilter !== 'all') searchParams.set('signalFilter', signalFilter);
    if (viewMode === 'map' && fiveGApFilter !== 'all') searchParams.set('fiveGApFilter', fiveGApFilter);
    if (viewMode === 'map' && childrenAllowedFilter !== 'all') searchParams.set('childrenAllowedFilter', childrenAllowedFilter);
    if (viewMode === 'map' && segmentFilter !== 'all') searchParams.set('segmentFilter', segmentFilter);
    if (viewMode === 'map' && signalMinRssi.trim() !== '') searchParams.set('signalMinRssi', signalMinRssi.trim());
    if (searchQuery.trim() !== '') searchParams.set('searchQuery', searchQuery);
    if (currentPage !== 1) searchParams.set('currentPage', currentPage.toString());
    if (itemsPerPage !== 10) searchParams.set('itemsPerPage', itemsPerPage.toString());
    
    // Serialize expanded devices state
    const expandedDevices = Object.keys(expandedCaps).filter(key => expandedCaps[key]);
    if (expandedDevices.length > 0) {
      searchParams.set('expanded', expandedDevices.join(','));
    }
    
    const queryString = searchParams.toString();
    const newUrl = `/${queryString ? `?${queryString}` : ''}`;
    
    // Only update URL if it's different from current URL
    if (location.pathname + location.search !== newUrl) {
      navigate(newUrl, { replace: true });
    }
  }, [viewMode, connectionFilter, deviceTypeFilter, signalFilter, fiveGApFilter, childrenAllowedFilter, segmentFilter, signalMinRssi, searchQuery, currentPage, itemsPerPage, expandedCaps, navigate, location]);

  // Helper function to navigate to device details with preserved filters
  const navigateToDevice = (deviceSerial) => {
    const searchParams = new URLSearchParams();
    if (viewMode !== 'map') searchParams.set('viewMode', viewMode);
    if (connectionFilter !== 'all') searchParams.set('connectionFilter', connectionFilter);
    if (deviceTypeFilter !== 'network') searchParams.set('deviceTypeFilter', deviceTypeFilter);
    if (viewMode === 'map' && signalFilter !== 'all') searchParams.set('signalFilter', signalFilter);
    if (viewMode === 'map' && fiveGApFilter !== 'all') searchParams.set('fiveGApFilter', fiveGApFilter);
    if (viewMode === 'map' && childrenAllowedFilter !== 'all') searchParams.set('childrenAllowedFilter', childrenAllowedFilter);
    if (viewMode === 'map' && segmentFilter !== 'all') searchParams.set('segmentFilter', segmentFilter);
    if (viewMode === 'map' && signalMinRssi.trim() !== '') searchParams.set('signalMinRssi', signalMinRssi.trim());
    if (searchQuery.trim() !== '') searchParams.set('searchQuery', searchQuery);
    if (currentPage !== 1) searchParams.set('currentPage', currentPage.toString());
    if (itemsPerPage !== 10) searchParams.set('itemsPerPage', itemsPerPage.toString());
    
    // Serialize expanded devices state
    const expandedDevices = Object.keys(expandedCaps).filter(key => expandedCaps[key]);
    if (expandedDevices.length > 0) {
      searchParams.set('expanded', expandedDevices.join(','));
    }
    
    const queryString = searchParams.toString();
    navigate(`/device/${deviceSerial}${queryString ? `?${queryString}` : ''}`);
  };

  const handleDeviceClick = (device) => {
    onDeviceSelect(device);
    navigateToDevice(device.serial_number);
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      if (newMode === 'map' && deviceTypeFilter !== 'network') {
        setDeviceTypeFilter('network');
      }
    }
  };

  const toggleCapExpansion = (capSerial) => {
    setExpandedCaps(prev => ({
      ...prev,
      [capSerial]: !prev[capSerial]
    }));
  };

  const getPeerAddress = (address) => {
    if (!address) return '';
    // Remove port number if present
    return address.split(':')[0];
  };

  const getDeviceType = (capabilities) => {
    if (!capabilities) {
      return 'N/A';
    }
    if (typeof capabilities === 'object') {
      const model = capabilities.model;
      if (model) {
        const modelLower = model.toLowerCase();
        if (modelLower.includes('juganucap')) {
          return 'CAP';
        } else if (modelLower.includes('juganunode')) {
          return 'Node';
        } else if (modelLower.includes('nvidia') || modelLower.includes('qrb5165-128gb')) {
          return 'AI';
        } else if (modelLower.includes('edgecore') || modelLower.includes('eap102')) {
          return 'EdgeCore';
        }
      }
      // Also check compatible field for EdgeCore devices
      if (capabilities.compatible === 'edgecore_eap102') {
        return 'EdgeCore';
      }
    }
    return 'N/A';
  };

  const getStaRssi = (device) => {
    const ifaces = device.private_wifi?.interfaces || device.private_wifi?.Interfaces;
    if (!ifaces) {
      return 'N/A';
    }
    const stationInterface = ifaces.find(iface => iface.Mode === 'station');
    if (!stationInterface?.RSSI) return 'N/A';
    return `${stationInterface.RSSI}`;
  };

  const getCorrespondingAIDeviceSerial = (networkDevice) => {
    // Only show AI device for network devices (not AI devices themselves)
    if (getDeviceType(networkDevice.capabilities) === 'AI') {
      return null;
    }
    
    // Use the ai_device_mac field directly from the device structure
    return networkDevice.ai_device_mac || null;
  };

  const getDeviceStatusChips = (device) => (
    <Box component="div" sx={{ display: 'flex', gap: 1 }}>
      <Chip
        label={device.compatible}
        color={device.compatible === 'compatible' ? 'success' : 'error'}
        size="small"
        variant="outlined"
      />
      <Chip
        label={getDeviceType(device.capabilities)}
        color={
          getDeviceType(device.capabilities) === 'CAP' ? 'secondary' :
          getDeviceType(device.capabilities) === 'AI' ? 'info' :
          getDeviceType(device.capabilities) === 'EdgeCore' ? 'warning' :
          'default'
        }
        size="small"
        variant="outlined"
      />
    </Box>
  );

  const renderDeviceInfo = (device) => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          marginRight: 2,
          ...statusDotSx(device.connected),
        }}
      />
      <Box>
        <Typography component="div" variant="body1">
          {getDeviceDisplayName(device)}
        </Typography>
        <Typography component="div" variant="caption" color="text.secondary">
          {device.serial_number}
        </Typography>
        <Typography component="div" variant="caption" color="text.secondary">
          {getPeerAddress(device.peer_address) || 'No IP'}
        </Typography>
      </Box>
    </Box>
  );

  const handleConnectionFilterChange = (event) => {
    setConnectionFilter(event.target.value);
  };

  const handleDeviceTypeFilterChange = (event) => {
    setDeviceTypeFilter(event.target.value);
  };

  const handleSignalFilterChange = (event) => {
    const value = event.target.value;
    setSignalFilter(value);
    if (value === 'all') {
      setSignalMinRssi('');
    }
  };

  const handleSignalMinRssiChange = (event) => {
    setSignalMinRssi(event.target.value);
  };

  const handleFiveGApFilterChange = (event) => {
    setFiveGApFilter(event.target.value);
  };

  const handleChildrenAllowedFilterChange = (event) => {
    setChildrenAllowedFilter(event.target.value);
  };

  const handleSegmentFilterChange = (event) => {
    setSegmentFilter(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const getFilteredDevices = () => {

    if (!devices) return [];
    return devices.filter(device => {
      // Apply connection status filter
      if (connectionFilter !== 'all' && device.connected !== (connectionFilter === 'connected')) {
        return false;
      }
      
      // Apply device type filter
      if (deviceTypeFilter !== 'all') {
        if (deviceTypeFilter === 'network') {
          if (device.compatible !== 'jnet2' && device.compatible !== 'edgecore_eap102') {
            return false;
          }
        } else {
          const deviceType = getDeviceType(device.capabilities);
          if (deviceTypeFilter !== deviceType) {
            return false;
          }
        }
      }

      // Apply search filter (supports comma-delimited OR searches)
      if (searchQuery.trim() !== '') {
        const queries = searchQuery.split(',').map(q => q.toLowerCase().trim()).filter(q => q !== '');
        const serialNumber = device.serial_number?.toLowerCase() || '';
        const firmware = device.firmware?.toLowerCase() || '';
        
        // Check if any of the search terms match (OR condition)
        const matchesAnyQuery = queries.some(query => 
          serialNumber.includes(query) || firmware.includes(query)
        );
        
        if (!matchesAnyQuery) {
          return false;
        }
      }

      // Map view: backhaul signal filter (AP / STA / both must meet min RSSI)
      if (viewMode === 'map' && signalFilter !== 'all') {
        const minRssi = parseInt(signalMinRssi, 10);
        if (!devicePassesSignalFilter(device, signalFilter, minRssi, devices)) {
          return false;
        }
      }

      // Map view: 5 GHz AP configured (configuration.ap[], not live private_wifi)
      if (viewMode === 'map' && fiveGApFilter !== 'all') {
        if (!devicePassesFiveGApConfigFilter(device, fiveGApFilter)) {
          return false;
        }
      }

      // Map view: treemgmt max_children (stored config, not state report)
      if (viewMode === 'map' && childrenAllowedFilter !== 'all') {
        if (!devicePassesChildrenAllowedConfigFilter(device, childrenAllowedFilter)) {
          return false;
        }
      }

      // Map view: custom-tree segment membership (roster of nodes + CAPs)
      if (viewMode === 'map' && segmentFilter !== 'all') {
        if (!devicePassesSegmentFilter(device, segmentFilter, customTrees)) {
          return false;
        }
      }
      
      return true;
    });
  };

  const getPaginatedDevices = () => {
    const filtered = getFilteredDevices();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const filteredCount = getFilteredDevices().length;
    return Math.ceil(filteredCount / itemsPerPage);
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const renderFilters = () => (
    <Stack 
      direction="row" 
      spacing={0.75}
      sx={{ 
        mb: 1.5,
        mt: 1.5,
        px: 2,
        alignItems: 'flex-end',
        flexWrap: 'nowrap',
        gap: 0.75,
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': { height: 4 },
      }}
    >
      <FilterField label="Search">
        <TextField
          size="small"
          placeholder="Serial, firmware…"
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            width: 140,
            '& .MuiOutlinedInput-root': { height: 32, fontSize: '0.75rem' },
            '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
          }}
        />
      </FilterField>

      <FilterField label="Connection">
        <FormControl size="small" sx={{ ...compactFilterFormSx, minWidth: 96 }}>
          <Select
            value={connectionFilter}
            onChange={handleConnectionFilterChange}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="connected">Connected</MenuItem>
            <MenuItem value="disconnected">Disconnected</MenuItem>
          </Select>
        </FormControl>
      </FilterField>

      <FilterField label="Type">
        <FormControl size="small" sx={{ ...compactFilterFormSx, minWidth: 88 }}>
          <Select
            value={deviceTypeFilter}
            onChange={handleDeviceTypeFilterChange}
          >
            <MenuItem value="network">Network</MenuItem>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="CAP">CAP</MenuItem>
            <MenuItem value="Node">Node</MenuItem>
            <MenuItem value="AI">AI</MenuItem>
            <MenuItem value="EdgeCore">EdgeCore</MenuItem>
          </Select>
        </FormControl>
      </FilterField>

      {viewMode === 'map' && (
        <>
          <FilterField label="Signal">
            <FormControl size="small" sx={{ ...compactFilterFormSx, minWidth: 72 }}>
              <Select
                value={signalFilter}
                onChange={handleSignalFilterChange}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="ap">AP</MenuItem>
                <MenuItem value="sta">STA</MenuItem>
                <MenuItem value="both">Both</MenuItem>
              </Select>
            </FormControl>
          </FilterField>
          {signalFilter !== 'all' && (
            <FilterField
              label="Min RSSI"
              helpTitle="Show nodes with backhaul RSSI at or above this value (dBm). CAP devices are always shown. AP = parent hears child; STA = child hears parent; Both = both must meet the threshold."
              helpLabel="Min RSSI help"
            >
              <TextField
                size="small"
                placeholder="-80"
                value={signalMinRssi}
                onChange={handleSignalMinRssiChange}
                sx={{
                  width: 64,
                  '& .MuiOutlinedInput-root': { height: 32, fontSize: '0.75rem' },
                  '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                }}
                type="number"
                inputProps={{ max: 0, step: 1, 'aria-label': 'Min RSSI dBm' }}
              />
            </FilterField>
          )}
          <FilterField
            label="5G SSID"
            helpTitle="Uses stored device configuration (configuration.ap[]), not live WiFi state. Yes = 5 GHz AP entry in config, or any CAP device. No = no 5 GHz AP entry in config."
            helpLabel="5G SSID assigned help"
          >
            <FormControl size="small" sx={{ ...compactFilterFormSx, minWidth: 68 }}>
              <Select
                value={fiveGApFilter}
                onChange={handleFiveGApFilterChange}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </FilterField>
          <FilterField
            label="Children"
            helpTitle="Uses stored treemgmt configuration (max_children), not live state. Yes = at least one treemgmt entry allows mesh children (max_children unset or not 0). No = at least one entry blocks children (max_children = 0)."
            helpLabel="Children allowed help"
          >
            <FormControl size="small" sx={{ ...compactFilterFormSx, minWidth: 68 }}>
              <Select
                value={childrenAllowedFilter}
                onChange={handleChildrenAllowedFilterChange}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </FilterField>
          <FilterField
            label="Segment"
            helpTitle="Show only devices assigned to the selected custom-tree segment (nodes and CAPs in that segment's roster)."
            helpLabel="Segment filter help"
          >
            <FormControl
              size="small"
              sx={{ ...compactFilterFormSx, minWidth: 80, maxWidth: 110 }}
              disabled={customTreesLoading}
            >
              <Select
                value={segmentFilter}
                onChange={handleSegmentFilterChange}
                renderValue={(value) => {
                  if (value === 'all') return 'All';
                  const tree = customTrees.find((t) => t.id === value);
                  return tree?.name || tree?.id || value;
                }}
              >
                <MenuItem value="all">All</MenuItem>
                {customTrees.map((tree) => {
                  const memberCount = (tree.devices?.length || 0) + (tree.caps?.length || 0);
                  const label = tree.name || tree.id;
                  const segmentLabel = tree.segment_id != null ? ` (${tree.segment_id})` : '';
                  return (
                    <MenuItem key={tree.id} value={tree.id}>
                      {label}{segmentLabel} — {memberCount} device{memberCount === 1 ? '' : 's'}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </FilterField>
        </>
      )}

      {viewMode === 'list' && (
        <FilterField label="Per page">
          <FormControl size="small" sx={{ ...compactFilterFormSx, minWidth: 56 }}>
            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </FilterField>
      )}

      <Box sx={{ flexGrow: 1, minWidth: 8, flexShrink: 0 }} />

      <IconButton
        size="small"
        onClick={() => onRefresh && onRefresh({ force: true })}
        sx={{ 
          bgcolor: 'primary.main',
          color: 'white',
          width: 32,
          height: 32,
          padding: 0,
          '&:hover': {
            bgcolor: 'primary.dark',
          },
          '&.Mui-disabled': {
            bgcolor: 'action.disabledBackground',
            color: 'action.disabled'
          }
        }}
      >
        <RefreshIcon fontSize="small" />
      </IconButton>

      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={handleViewModeChange}
        size="small"
        sx={{
          flexShrink: 0,
          '& .MuiToggleButton-root': {
            px: 1.25,
            py: 0.25,
            minHeight: 32,
            fontSize: '0.7rem',
            fontWeight: 600,
            textTransform: 'none',
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            '&.Mui-selected': {
              backgroundColor: 'primary.main',
              color: 'white',
              borderColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            },
          },
        }}
      >
        <ToggleButton value="map">Map</ToggleButton>
        <ToggleButton value="list">List</ToggleButton>
        <ToggleButton value="hierarchical">Hierarchy</ToggleButton>
        <ToggleButton value="radio">Radio</ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );

  const renderPaginationInfo = () => (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      px: 2,
      py: 1,
      backgroundColor: 'grey.50',
      borderRadius: 1,
      mb: 2
    }}>
      <Typography variant="body2" color="text.secondary">
        Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, getFilteredDevices().length)} of {getFilteredDevices().length} devices
      </Typography>
      
      {getTotalPages() > 1 && (
        <Pagination
          count={getTotalPages()}
          page={currentPage}
          onChange={handlePageChange}
          size="small"
          showFirstButton
          showLastButton
        />
      )}
    </Box>
  );

  const renderListView = () => {
    const paginatedDevices = getPaginatedDevices();
    const isAIFilter = deviceTypeFilter === 'AI';
    const isCAPFilter = deviceTypeFilter === 'CAP';
    
    // Define grid templates based on filter
    const actionsCol = canWriteBackhaul ? ' 60px' : '';
    let gridTemplateColumns;
    if (isAIFilter) {
      gridTemplateColumns = `180px 120px 120px 140px 90px${actionsCol}`; // Hide Uplink, Signal, AI Device columns
    } else if (isCAPFilter) {
      gridTemplateColumns = `180px 120px 140px 90px 120px${actionsCol}`; // Hide Uplink, Parent, Signal columns
    } else {
      gridTemplateColumns = `180px 120px 120px 120px 140px 140px 90px 120px${actionsCol}`; // Show all columns
    }
    
    return (
      <Box>
        {/* Header */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns,
          gap: 1,
          px: 2,
          py: 0.75,
          mb: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="subtitle2" color="text.secondary">Device</Typography>
          <Typography variant="subtitle2" color="text.secondary">IP Address</Typography>
          {!isCAPFilter && <Typography variant="subtitle2" color="text.secondary">Parent</Typography>}
          {!isAIFilter && !isCAPFilter && <Typography variant="subtitle2" color="text.secondary">Uplink</Typography>}
          {!isAIFilter && !isCAPFilter && <Typography variant="subtitle2" color="text.secondary">Signal</Typography>}
          <Typography variant="subtitle2" color="text.secondary">Health</Typography>
          <Typography variant="subtitle2" color="text.secondary">Type</Typography>
          {!isAIFilter && <Typography variant="subtitle2" color="text.secondary">AI Device</Typography>}
          {canWriteBackhaul && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ textAlign: 'center' }}>Actions</Typography>
          )}
        </Box>

        {/* Device Rows */}
        {paginatedDevices.map((device) => {
          const stationInterface = (device.private_wifi?.interfaces || device.private_wifi?.Interfaces)?.find(iface => iface.Mode === 'station');
          return (
            <Paper
              key={device.serial_number}
              elevation={0}
              sx={{ 
                mb: 0.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                transition: 'all 0.2s ease-in-out',
                backgroundColor: (theme) => {
                  if (getDeviceType(device.capabilities) !== 'CAP') return 'transparent';
                  return theme.palette.mode === 'dark' ? theme.palette.action.selected : theme.palette.grey[100];
                },
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: 1,
                  borderColor: 'primary.main',
                  cursor: 'pointer'
                }
              }}
              onClick={() => handleDeviceClick(device)}
            >
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns,
                gap: 1,
                p: 1,
                alignItems: 'center',
                minHeight: '90px'
              }}>
                {/* Device Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      mr: 1.5,
                      flexShrink: 0,
                      ...statusDotSx(device.connected),
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {device.serial_number}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.25 }}>
                      {device.firmware || 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                {/* IP Address */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip
                    label={getPeerAddress(device.peer_address) || 'No IP'}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      height: 20,
                      maxWidth: '100%',
                      '& .MuiChip-label': { 
                        px: 0.5,
                        py: 0,
                        fontSize: '0.75rem'
                      }
                    }}
                  />
                </Box>

                {/* Parent */}
                {!isCAPFilter && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      label={device.parent_serial || 'None'}
                      size="small"
                      variant="outlined"
                      sx={{ 
                        height: 20,
                        maxWidth: '100%',
                        '& .MuiChip-label': { 
                          px: 0.5,
                          py: 0,
                          fontSize: '0.75rem'
                        }
                      }}
                    />
                  </Box>
                )}

                {/* Uplink */}
                {!isAIFilter && !isCAPFilter && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      label={device.ul_channel === -1 || device.ul_channel === undefined || device.ul_channel === null ? 
                        'N/A' : 
                        `${device.ul_channel} (${device.ul_band || 'N/A'})`}
                      size="small"
                      variant="outlined"
                      sx={{ 
                        height: 20,
                        maxWidth: '100%',
                        '& .MuiChip-label': { 
                          px: 0.5,
                          py: 0,
                          fontSize: '0.75rem'
                        }
                      }}
                    />
                  </Box>
                )}

                {/* Signal */}
                {!isAIFilter && !isCAPFilter && (
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {stationInterface?.RSSI && (
                      <Tooltip title="RSSI measured by station radio (RX)">
                        <Chip
                          label={`${stationInterface.RSSI}dBm`}
                          size="small"
                          sx={{
                            ...healthChipSoftSx(stationInterface.RSSI < -80 ? 'error' : 'success'),
                            '& .MuiChip-label': {
                              px: 0.5,
                              py: 0,
                              fontSize: '0.75rem',
                            },
                          }}
                        />
                      </Tooltip>
                    )}
                    {(() => {
                      const parentDevice = devices.find(d => 
                        d.serial_number.toLowerCase() === device.parent_serial?.toLowerCase()
                      );
                      if (!parentDevice) return null;

                      const deviceMacs = device.radios?.map(radio => radio.mac?.toLowerCase()).filter(Boolean);
                      if (!deviceMacs?.length) return null;

                      const findTxRssi = (interfaces) => {
                        if (!interfaces) return null;
                        for (const iface of interfaces) {
                          if (!iface.Associations) continue;
                          for (const assoc of iface.Associations) {
                            if (!assoc.Station) continue;
                            const stationMac = assoc.Station.toLowerCase();
                            if (deviceMacs.includes(stationMac)) {
                              return assoc.RSSI;
                            }
                          }
                        }
                        return null;
                      };

                      const txRssi = findTxRssi(parentDevice.private_wifi?.interfaces || parentDevice.private_wifi?.Interfaces) || 
                                    findTxRssi(parentDevice.public_wifi?.interfaces || parentDevice.public_wifi?.Interfaces);

                      if (txRssi) {
                        return (
                          <Tooltip title="RSSI as seen by parent AP (TX)">
                            <Chip
                              label={`${txRssi}dBm`}
                              size="small"
                              sx={{
                                ...healthChipSoftSx(txRssi < -80 ? 'error' : 'success'),
                                '& .MuiChip-label': {
                                  px: 0.5,
                                  py: 0,
                                  fontSize: '0.75rem',
                                },
                              }}
                            />
                          </Tooltip>
                        );
                                            }
                        return null;
                     })()}
                  </Box>
                )}

                {/* Health */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {device.backhaul_tx_ratio !== undefined && device.backhaul_tx_ratio !== null ? (
                      <Tooltip title="TX sent vs pending">
                        <Chip
                          label={`${(device.backhaul_tx_ratio * 100).toFixed(0)}%`}
                          size="small"
                          sx={{
                            ...healthChipSoftSx(
                              device.backhaul_tx_ratio >= 0.8 ? 'success' :
                              device.backhaul_tx_ratio >= 0.6 ? 'warning' : 'error'
                            ),
                            '& .MuiChip-label': {
                              px: 0.5,
                              py: 0,
                              fontSize: '0.75rem',
                            },
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        label="N/A"
                        size="small"
                        variant="outlined"
                        sx={{ 
                          height: 20,
                          '& .MuiChip-label': { 
                            px: 0.5,
                            py: 0,
                            fontSize: '0.75rem'
                          }
                        }}
                      />
                    )}
                    {device.backhaul_tx_failed_percentage !== undefined && device.backhaul_tx_failed_percentage !== null && (
                      <Tooltip title={`TX Failed: ${(device.backhaul_tx_failed_percentage * 100).toFixed(1)}%`}>
                        <Chip
                          label={`F${(device.backhaul_tx_failed_percentage * 100).toFixed(0)}%`}
                          size="small"
                          sx={{
                            ...healthChipSoftSx(
                              device.backhaul_tx_failed_percentage <= 0.1 ? 'success' :
                              device.backhaul_tx_failed_percentage <= 0.3 ? 'warning' : 'error'
                            ),
                            '& .MuiChip-label': {
                              px: 0.5,
                              py: 0,
                              fontSize: '0.75rem',
                            },
                          }}
                        />
                      </Tooltip>
                    )}
                    {(() => {
                      const parentMs = getParentLatencyMs(device);
                      if (parentMs === null) return null;
                      return (
                        <Tooltip title={formatLatencyTooltip(device.latency?.to_parent, 'Latency to parent')}>
                          <Chip
                            label={`P ${formatLatencyMs(parentMs)}`}
                            size="small"
                            sx={{
                              ...healthChipSoftSx(getLatencyChipColor(parentMs)),
                              '& .MuiChip-label': {
                                px: 0.5,
                                py: 0,
                                fontSize: '0.75rem',
                              },
                            }}
                          />
                        </Tooltip>
                      );
                    })()}
                    {(() => {
                      const capMs = getCapLatencyMs(device);
                      if (capMs === null) return null;
                      return (
                        <Tooltip title={formatLatencyTooltip(device.latency?.to_cap, 'Latency to CAP')}>
                          <Chip
                            label={`C ${formatLatencyMs(capMs)}`}
                            size="small"
                            sx={{
                              ...healthChipSoftSx(getLatencyChipColor(capMs)),
                              '& .MuiChip-label': {
                                px: 0.5,
                                py: 0,
                                fontSize: '0.75rem',
                              },
                            }}
                          />
                        </Tooltip>
                      );
                    })()}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {device.uptime && (
                      <Tooltip title={`Uptime: ${(() => {
                        const days = Math.floor(device.uptime / 86400);
                        const hours = Math.floor((device.uptime % 86400) / 3600);
                        const minutes = Math.floor((device.uptime % 3600) / 60);
                        
                        if (days > 0) {
                          return days >= 7 ? `${Math.floor(days / 7)}w ${days % 7}d` : `${days}d ${hours}h`;
                        } else if (hours > 0) {
                          return `${hours}h ${minutes}m`;
                        } else {
                          return `${minutes}m`;
                        }
                      })()}`}>
                        <Chip
                          label={(() => {
                            const days = Math.floor(device.uptime / 86400);
                            if (days >= 7) {
                              return `${Math.floor(days / 7)}w`;
                            } else if (days > 0) {
                              return `${days}d`;
                            } else {
                              return `${Math.floor(device.uptime / 3600)}h`;
                            }
                          })()}
                          size="small"
                          variant="outlined"
                          color="info"
                          sx={{ 
                            height: 18,
                            '& .MuiChip-label': { 
                              px: 0.5,
                              py: 0,
                              fontSize: '0.7rem'
                            }
                          }}
                        />
                      </Tooltip>
                    )}
                    {device.last_seen && (
                      <Tooltip title={`Last seen: ${new Date(device.last_seen).toLocaleString()}`}>
                        <Chip
                          label={(() => {
                            const now = Date.now();
                            const lastSeenTime = new Date(device.last_seen).getTime();
                            const diff = (now - lastSeenTime) / 1000; // Convert to seconds
                            if (diff < 60) return 'now';
                            if (diff < 3600) return `${Math.floor(diff / 60)}m`;
                            if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
                            return `${Math.floor(diff / 86400)}d`;
                          })()}
                          size="small"
                          sx={{
                            ...healthChipSoftSx((() => {
                              const now = Date.now();
                              const lastSeenTime = new Date(device.last_seen).getTime();
                              const diff = (now - lastSeenTime) / 1000;
                              if (diff < 300) return 'success';
                              if (diff < 3600) return 'warning';
                              return 'error';
                            })()),
                            height: 18,
                            '& .MuiChip-label': {
                              px: 0.5,
                              py: 0,
                              fontSize: '0.7rem',
                            },
                          }}
                        />
                      </Tooltip>
                    )}
                    {(() => {
                      // Find parent device to get connected time
                      if (!device.parent_serial) return null;
                      
                      const parentDevice = devices.find(d => 
                        d.serial_number && 
                        d.serial_number.toLowerCase() === device.parent_serial.toLowerCase()
                      );
                      
                      if (!parentDevice || (!parentDevice.private_wifi?.interfaces && !parentDevice.private_wifi?.Interfaces)) return null;

                      // Get device's station interface MAC address (the one that connects to parent)
                      const stationInterfaces = device.private_wifi?.interfaces || device.private_wifi?.Interfaces;
                      const stationInterface = stationInterfaces?.find(iface => iface.Mode === 'station');
                      const deviceMac = stationInterface?.MAC?.toLowerCase();
                      if (!deviceMac) return null;

                      // Search parent's WiFi associations for this MAC
                      const findConnectedTime = () => {
                        const parentIfaces = parentDevice.private_wifi?.interfaces || parentDevice.private_wifi?.Interfaces;
                        for (const iface of parentIfaces || []) {
                          if (iface.Associations) {
                            for (const assoc of iface.Associations) {
                              if (assoc.Station?.toLowerCase() === deviceMac && assoc.ConnectedTime) {
                                return assoc.ConnectedTime;
                              }
                            }
                          }
                        }
                        return null;
                      };

                      const connectedTime = findConnectedTime();
                      
                      if (!connectedTime) return null;
                      
                      return (
                        <Tooltip title={`Connected: ${(() => {
                          const days = Math.floor(connectedTime / 86400);
                          const hours = Math.floor((connectedTime % 86400) / 3600);
                          const minutes = Math.floor((connectedTime % 3600) / 60);
                          
                          if (days > 0) {
                            return days >= 7 ? `${Math.floor(days / 7)}w ${days % 7}d` : `${days}d ${hours}h`;
                          } else if (hours > 0) {
                            return `${hours}h ${minutes}m`;
                          } else {
                            return `${minutes}m`;
                          }
                        })()}`}>
                          <Chip
                            label={(() => {
                              const days = Math.floor(connectedTime / 86400);
                              if (days >= 7) {
                                return `C${Math.floor(days / 7)}w`;
                              } else if (days > 0) {
                                return `C${days}d`;
                              } else {
                                return `C${Math.floor(connectedTime / 3600)}h`;
                              }
                            })()}
                            size="small"
                            variant="outlined"
                            color="secondary"
                            sx={{ 
                              height: 18,
                              '& .MuiChip-label': { 
                                px: 0.5,
                                py: 0,
                                fontSize: '0.7rem'
                              }
                            }}
                          />
                        </Tooltip>
                      );
                    })()}
                  </Box>
                </Box>

                {/* Type - Made more compact */}
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', overflow: 'hidden' }}>
                  <Stack direction="row" spacing={0.5} sx={{ minWidth: 0 }}>
                    <Chip
                      label={device.compatible}
                      size="small"
                      color={device.compatible === 'jnet2' ? 'primary' : 'default'}
                      variant="outlined"
                      sx={{ 
                        height: 20,
                        minWidth: 0,
                        '& .MuiChip-label': { 
                          px: 0.5,
                          py: 0,
                          fontSize: '0.75rem',
                          textOverflow: 'ellipsis'
                        }
                      }}
                    />
                    <Chip
                      label={getDeviceType(device.capabilities)}
                      size="small"
                      color={
                        getDeviceType(device.capabilities) === 'CAP' ? 'secondary' :
                        getDeviceType(device.capabilities) === 'AI' ? 'info' :
                        'default'
                      }
                      variant="outlined"
                      sx={{ 
                        height: 20,
                        minWidth: 0,
                        '& .MuiChip-label': { 
                          px: 0.5,
                          py: 0,
                          fontSize: '0.75rem',
                          textOverflow: 'ellipsis'
                        }
                      }}
                    />
                  </Stack>
                </Box>

                {/* AI Device */}
                {!isAIFilter && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {(() => {
                      const aiDeviceSerial = getCorrespondingAIDeviceSerial(device);
                      if (!aiDeviceSerial) {
                        return (
                          <Chip
                            label="None"
                            size="small"
                            variant="outlined"
                            sx={{ 
                              height: 20,
                              maxWidth: '100%',
                              '& .MuiChip-label': { 
                                px: 0.5,
                                py: 0,
                                fontSize: '0.75rem'
                              }
                            }}
                          />
                        );
                      }
                      
                      return (
                        <Tooltip title={`AI Device: ${aiDeviceSerial}`}>
                          <Chip
                            label={aiDeviceSerial}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              maxWidth: '100%',
                              '& .MuiChip-label': {
                                px: 0.5,
                                py: 0,
                                fontSize: '0.75rem',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                        </Tooltip>
                      );
                    })()}
                  </Box>
                )}

                {canWriteBackhaul && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%'
                }}>
                  <IconButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(device);
                    }}
                    size="small"
                    sx={{
                      color: 'error.main',
                      '&:hover': {
                        bgcolor: 'error.lighter'
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>
    );
  };

  const renderHierarchicalView = () => {
    // First get the base filtered devices
    const baseFilteredDevices = getFilteredDevices();
    
    // Special handling for AI device filter in hierarchical view
    let filteredDevices;
    let isAIHierarchicalView = false;
    
    if (deviceTypeFilter === 'AI') {
      // For AI devices, we want to show the network hierarchy but only display AI devices
      // We'll create virtual network devices to represent the hierarchy structure
      const aiDevices = baseFilteredDevices.filter(device => device.connected === true);
      const parentSerials = [...new Set(aiDevices.map(device => device.parent_serial).filter(Boolean))];
      
      // Get all network devices that are parents/ancestors of AI devices
      const allNetworkDevices = devices.filter(device => 
        device.connected === true && 
        (device.compatible === 'jnet2' || device.compatible === 'edgecore_eap102')
      );
      
      // Build the complete hierarchy of network devices that have AI children
      const relevantNetworkDevices = new Set();
      
      const addDeviceAndAncestors = (deviceSerial) => {
        const device = allNetworkDevices.find(d => 
          d.serial_number.toLowerCase() === deviceSerial.toLowerCase()
        );
        if (device && !relevantNetworkDevices.has(device.serial_number)) {
          relevantNetworkDevices.add(device.serial_number);
          // Add parent hierarchy (upward)
          if (device.parent_serial) {
            addDeviceAndAncestors(device.parent_serial);
          }
        }
      };
      
      // For each AI parent, add the complete ancestor chain
      parentSerials.forEach(parentSerial => {
        addDeviceAndAncestors(parentSerial);
      });
      
      // Create the filtered devices array with network hierarchy + AI devices
      filteredDevices = [
        ...Array.from(relevantNetworkDevices).map(serial => 
          allNetworkDevices.find(d => d.serial_number === serial)
        ).filter(Boolean),
        ...aiDevices
      ];
      
      isAIHierarchicalView = true;
    } else {
      // Regular hierarchical view for non-AI filters
      filteredDevices = baseFilteredDevices.filter(device => device.connected === true);
    }
    


    // Group devices by parent (case-insensitive)
    const devicesByParent = filteredDevices.reduce((acc, device) => {
      const parentSerial = device.parent_serial ? device.parent_serial.toLowerCase() : 'root';
      if (!acc[parentSerial]) {
        acc[parentSerial] = [];
      }
      acc[parentSerial].push(device);
      return acc;
    }, {});



    // Build a set of devices that are children of EdgeCore APs (derived from associations)
    const edgeCoreChildSerials = new Set();
    filteredDevices.forEach(d => {
      const isEdgeCore = d.compatible && d.compatible.toLowerCase().includes('edgecore');
      if (!isEdgeCore) return;
      const ifaces = d?.private_wifi?.interfaces || d?.private_wifi?.Interfaces || [];
      ifaces.forEach(iface => {
        const assocs = iface.Associations || iface.associations || [];
        assocs.forEach(assoc => {
          const s = assoc.Station || assoc.station;
          if (!s) return;
          const key = s.replace(/:/g, '').toLowerCase();
          if (key) edgeCoreChildSerials.add(key);
        });
      });
    });

    // Get root devices (devices with no parent) but exclude those that are EdgeCore children
    const allRootDevices = (devicesByParent['root'] || []).filter(d => {
      const serialKey = d.serial_number ? d.serial_number.toLowerCase() : '';
      const serialNoColon = serialKey.replace(/:/g, '');
      return !(edgeCoreChildSerials.has(serialKey) || edgeCoreChildSerials.has(serialNoColon));
    });
    
    // Separate CAP-like root devices (CAP and EdgeCore) from orphaned devices
    const isCapLike = (device) => {
      const t = getDeviceType(device.capabilities);
      return t === 'CAP' || (device.compatible && device.compatible.toLowerCase().includes('edgecore'));
    };
    const rootDevices = allRootDevices.filter(device => isCapLike(device));
    const orphanedDevices = allRootDevices.filter(device => 
      !isCapLike(device) && 
      !device.parent_serial
    );
    


    // Function to calculate total descendants (children + grandchildren + etc., excluding the device itself)
    // Using iterative approach to avoid stack overflow
    const calculateTotalDescendants = (rootDevice) => {
      const visited = new Set();
      const queue = [rootDevice];
      let totalCount = 0;
      
      visited.add(rootDevice.serial_number.toLowerCase());
      
      while (queue.length > 0) {
        const currentDevice = queue.shift();
        
        // Find child devices using both parent_serial and EdgeCore children arrays
        let childDevices = filteredDevices.filter(d => 
          d.parent_serial && 
          d.parent_serial.toLowerCase() === currentDevice.serial_number.toLowerCase()
        );

        // For EdgeCore devices, also derive children from associations
        const ecIfaces = currentDevice?.private_wifi?.interfaces || currentDevice?.private_wifi?.Interfaces || [];
        const ecStations = new Set();
        ecIfaces.forEach(iface => {
          const assocs = iface.Associations || iface.associations || [];
          assocs.forEach(assoc => {
            const s = assoc.Station || assoc.station;
            if (!s) return;
            const key = s.replace(/:/g, '').toLowerCase();
            if (key) ecStations.add(key);
          });
        });
        if (ecStations.size > 0) {
          const edgeCoreChildren = filteredDevices.filter(d => {
            const k1 = d.serial_number ? d.serial_number.toLowerCase() : '';
            const k2 = k1.replace(/:/g, '');
            return ecStations.has(k1) || ecStations.has(k2);
          });
          edgeCoreChildren.forEach(child => {
            if (!childDevices.find(existing => existing.serial_number.toLowerCase() === child.serial_number.toLowerCase())) {
              childDevices.push(child);
            }
          });
        }
        
        // Add unvisited children to queue and count them
        for (const child of childDevices) {
          if (!visited.has(child.serial_number.toLowerCase())) {
            visited.add(child.serial_number.toLowerCase());
            queue.push(child);
            totalCount++; // Count each descendant
          }
        }
      }
      
      return totalCount;
    };

    const renderDeviceTree = (device, depth = 0, visited = new Set()) => {
      // Prevent infinite loops from cycles or repeated references
      const key = device?.serial_number?.toLowerCase?.() || '';
      if (key && visited.has(key)) {
        return null;
      }
      if (key) visited.add(key);
      // Find child devices using both parent_serial and EdgeCore children arrays
      let childDevices = filteredDevices.filter(d => 
        d.parent_serial && 
        d.parent_serial.toLowerCase() === device.serial_number.toLowerCase()
      );

      // For EdgeCore devices, also derive children from associations
      const ecIfaces = device?.private_wifi?.interfaces || device?.private_wifi?.Interfaces || [];
      const ecStations = new Set();
      ecIfaces.forEach(iface => {
        const assocs = iface.Associations || iface.associations || [];
        assocs.forEach(assoc => {
          const s = assoc.Station || assoc.station;
          if (!s) return;
          const key = s.replace(/:/g, '').toLowerCase();
          if (key) ecStations.add(key);
        });
      });
      if (ecStations.size > 0) {
        const edgeCoreChildren = filteredDevices.filter(d => {
          const k1 = d.serial_number ? d.serial_number.toLowerCase() : '';
          const k2 = k1.replace(/:/g, '');
          return ecStations.has(k1) || ecStations.has(k2);
        });
        edgeCoreChildren.forEach(child => {
          if (!childDevices.find(existing => existing.serial_number.toLowerCase() === child.serial_number.toLowerCase())) {
            childDevices.push(child);
          }
        });
      }
      

      
      // In AI hierarchical view, show AI devices with network board context
      if (isAIHierarchicalView) {
        const isAIDevice = getDeviceType(device.capabilities) === 'AI';
        const isNetworkDevice = device.compatible === 'jnet2' || device.compatible === 'edgecore_eap102';
        
                  if (isNetworkDevice && !isAIDevice) {
            // This is a network device - render it with its hierarchy structure for AI children
            const aiChildren = childDevices.filter(child => getDeviceType(child.capabilities) === 'AI');
            const networkChildren = childDevices.filter(child => 
              child.compatible === 'jnet2' || child.compatible === 'edgecore_eap102'
            );
            
            // For non-root network devices that only have AI children, don't render the network device
            // Instead, render the AI children directly
            if (depth > 0 && aiChildren.length > 0 && networkChildren.length === 0) {
              return (
                <React.Fragment key={device.serial_number}>
                  {aiChildren.map(child => (
                    <Box key={child.serial_number} sx={{ borderLeft: `2px solid ${depth > 0 ? '#999' : '#666'}`, pl: 2 }}>
                      {renderDeviceTree(child, depth, new Set(visited))}
                    </Box>
                  ))}
                </React.Fragment>
              );
            }
            
            if (aiChildren.length === 0 && networkChildren.length === 0) {
              return null; // Don't render network devices with no relevant children
            }
          
          // Render the network device and its children
          return (
            <React.Fragment key={device.serial_number}>
              <ListItem
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    cursor: 'pointer'
                  },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 0.5,
                  ml: depth * 2,
                  backgroundColor: (theme) => {
                    const deviceType = getDeviceType(device.capabilities);
                    if (deviceType === 'CAP') {
                      return theme.palette.mode === 'dark' ? theme.palette.action.selected : theme.palette.grey[100];
                    }
                    if (deviceType === 'EdgeCore') {
                      return theme.palette.mode === 'dark' ? theme.palette.action.selected : 'warning.lighter';
                    }
                    return 'transparent';
                  },
                }}
                onClick={() => handleDeviceClick(device)}
                secondaryAction={
                  childDevices.length > 0 ? (
                    <IconButton 
                      edge="end" 
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCapExpansion(device.serial_number);
                      }}
                    >
                      {expandedCaps[device.serial_number] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  ) : null
                }
              >
                <ListItemText
                  primary={(
                    (() => {
                      if (isAIHierarchicalView && device.ai_device_mac) {
                        // Use the direct ai_device_mac field from the network device
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography component="div" variant="body1">
                              {device.ai_device_mac}
                            </Typography>
                            <Typography component="div" variant="caption" color="text.secondary">
                              Network Board: {device.serial_number}
                            </Typography>
                          </Box>
                        );
                      }
                      return renderDeviceInfo(device);
                    })()
                  )}
                  secondary={
                    <Box component="div" sx={{ mt: 1 }}>
                      {getDeviceStatusChips(device)}
                      {aiChildren.length > 0 && (
                        <Typography component="div" variant="caption" color="text.secondary">
                          AI Devices: {aiChildren.length}
                        </Typography>
                      )}
                    </Box>
                  }
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItem>
              {childDevices.length > 0 && (
                <Collapse in={expandedCaps[device.serial_number]}>
                  <List component="div" disablePadding>
                    {childDevices.map(child => (
                      <Box key={child.serial_number} sx={{ borderLeft: `2px solid ${depth > 0 ? '#999' : '#666'}`, pl: 2 }}>
                        {renderDeviceTree(child, depth + 1, new Set(visited))}
                      </Box>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        }
        
        // This is an AI device - render it with network board info
        if (isAIDevice) {
          // For AI devices, show children count based on their network board (parent_serial)
          // since the hierarchy is based on network board relationships
          const networkBoardSerial = device.parent_serial;
          let aiChildDevices = [];
          
          // Find the network board device
          const networkBoard = networkBoardSerial ? filteredDevices.find(d => 
            d.serial_number && 
            d.serial_number.toLowerCase() === networkBoardSerial.toLowerCase()
          ) : null;
          
          if (networkBoard) {
            // Calculate child devices for the network board using same logic as in renderDeviceTree
            aiChildDevices = filteredDevices.filter(d => 
              d.parent_serial && 
              d.parent_serial.toLowerCase() === networkBoard.serial_number.toLowerCase()
            );

            // For EdgeCore devices, also derive children from associations
            const ecIfaces = networkBoard?.private_wifi?.interfaces || networkBoard?.private_wifi?.Interfaces || [];
            const ecStations = new Set();
            ecIfaces.forEach(iface => {
              const assocs = iface.Associations || iface.associations || [];
              assocs.forEach(assoc => {
                const s = assoc.Station || assoc.station;
                if (!s) return;
                const key = s.replace(/:/g, '').toLowerCase();
                if (key) ecStations.add(key);
              });
            });
            if (ecStations.size > 0) {
              const edgeCoreChildren = filteredDevices.filter(d => {
                const k1 = d.serial_number ? d.serial_number.toLowerCase() : '';
                const k2 = k1.replace(/:/g, '');
                return ecStations.has(k1) || ecStations.has(k2);
              });
              edgeCoreChildren.forEach(child => {
                if (!aiChildDevices.find(existing => existing.serial_number.toLowerCase() === child.serial_number.toLowerCase())) {
                  aiChildDevices.push(child);
                }
              });
            }
          }
          return (
            <React.Fragment key={device.serial_number}>
              <ListItem
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    cursor: 'pointer'
                  },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 0.5,
                  ml: depth * 2,
                  backgroundColor: 'info.lighter',
                }}
                onClick={() => handleDeviceClick(device)}
                secondaryAction={
                  aiChildDevices.length > 0 ? (
                    <IconButton 
                      edge="end" 
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCapExpansion(device.serial_number);
                      }}
                    >
                      {expandedCaps[device.serial_number] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  ) : null
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          marginRight: 2,
                          ...statusDotSx(device.connected),
                        }}
                      />
                      <Box>
                        <Typography component="div" variant="body1">
                          {getDeviceDisplayName(device)}
                        </Typography>
                        <Typography component="div" variant="caption" color="text.secondary">
                          Type: {getDeviceType(device.capabilities)} | Compatible: {device.compatible}
                        </Typography>
                        {device.parent_serial && (
                          <Typography component="div" variant="caption" color="text.secondary">
                            Network Board: {device.parent_serial}
                          </Typography>
                        )}
                        <Typography component="div" variant="caption" color="text.secondary">
                          {getPeerAddress(device.peer_address) || 'No IP'}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Box component="div" sx={{ mt: 1 }}>
                      {getDeviceStatusChips(device)}
                      {/* Counts hidden for AI devices per requirement */}
                    </Box>
                  }
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItem>
              {aiChildDevices.length > 0 && (
                <Collapse in={expandedCaps[device.serial_number]}>
                  <List component="div" disablePadding>
                    {aiChildDevices.map(child => (
                      <Box key={child.serial_number} sx={{ borderLeft: `2px solid ${depth > 0 ? '#999' : '#666'}`, pl: 2 }}>
                        {renderDeviceTree(child, depth + 1, new Set(visited))}
                      </Box>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        }
        
        return null; // Skip other device types in AI view
      }
      
      return (
        <React.Fragment key={device.serial_number}>
          <ListItem
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                cursor: 'pointer'
              },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mb: 0.5,
              ml: depth * 2, // Indent based on depth
              backgroundColor: (theme) => {
                const deviceType = getDeviceType(device.capabilities);
                if (deviceType === 'CAP') {
                  return theme.palette.mode === 'dark' ? theme.palette.action.selected : theme.palette.grey[100];
                }
                if (deviceType === 'EdgeCore') {
                  return theme.palette.mode === 'dark' ? theme.palette.action.selected : 'warning.lighter';
                }
                return 'transparent';
              },
            }}
            onClick={() => handleDeviceClick(device)}
            secondaryAction={
              childDevices.length > 0 ? (
                <IconButton 
                  edge="end" 
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCapExpansion(device.serial_number);
                  }}
                >
                  {expandedCaps[device.serial_number] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              ) : null
            }
          >
            <ListItemText
              primary={(
              (() => {
                if (isAIHierarchicalView && device.ai_device_mac) {
                  // Use the direct ai_device_mac field from the network device
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                      <Typography component="div" variant="body1">
                        {device.ai_device_mac}
                      </Typography>
                      <Typography component="div" variant="caption" color="text.secondary">
                        Network Board: {device.serial_number}
                      </Typography>
                    </Box>
                  );
                }
                return renderDeviceInfo(device);
              })()
            )}
              secondary={
                <Box component="div" sx={{ mt: 1 }}>
                  {getDeviceStatusChips(device)}
                  {(() => {
                    // Show counts only for CAP devices
                    if (getDeviceType(device.capabilities) !== 'CAP') {
                      return null;
                    }
                    const totalDescendants = calculateTotalDescendants(device);
                    return (
                      <Typography component="div" variant="caption" color="text.secondary">
                        Direct Children: {childDevices.length} | Total in Tree: {totalDescendants}
                      </Typography>
                    );
                  })()}
                  {depth > 0 && (
                    <Typography component="div" variant="caption" color="text.secondary">
                      UL: {device.ul_channel === -1 || device.ul_channel === undefined || device.ul_channel === null ? 
                        'N/A' : 
                        `${device.ul_channel} (${device.ul_band || 'N/A'})`}
                      {getDeviceType(device.capabilities) === 'Node' && ` | RSSI: ${getStaRssi(device)}`}
                    </Typography>
                  )}
                </Box>
              }
              primaryTypographyProps={{ component: 'div' }}
              secondaryTypographyProps={{ component: 'div' }}
            />
          </ListItem>
          {childDevices.length > 0 && (
            <Collapse in={expandedCaps[device.serial_number]}>
              <List component="div" disablePadding>
                {childDevices.map(child => (
                  <Box key={child.serial_number} sx={{ borderLeft: `2px solid ${depth > 0 ? '#999' : '#666'}`, pl: 2 }}>
                    {renderDeviceTree(child, depth + 1, new Set(visited))}
                  </Box>
                ))}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    };

    // Function to render orphaned devices with special styling
    const renderOrphanedDevice = (device) => (
      <ListItem
        key={device.serial_number}
        sx={{
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            cursor: 'pointer'
          },
          border: '2px solid',
          borderColor: 'warning.main',
          borderRadius: 1,
          mb: 0.5,
          backgroundColor: 'warning.lighter',
          ml: 1, // Slight indentation to distinguish from hierarchy
        }}
        onClick={() => handleDeviceClick(device)}
      >
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  marginRight: 2,
                  ...statusDotSx(device.connected),
                }}
              />
              <Box>
                <Typography component="div" variant="body1" sx={{ fontWeight: 600 }}>
                  ⚠️ {getDeviceDisplayName(device)} (Orphaned)
                </Typography>
                <Typography component="div" variant="caption" color="text.secondary">
                  {device.serial_number}
                </Typography>
                <Typography component="div" variant="caption" color="text.secondary">
                  Type: {getDeviceType(device.capabilities)} | Compatible: {device.compatible}
                </Typography>
                <Typography component="div" variant="caption" color="text.secondary">
                  {getPeerAddress(device.peer_address) || 'No IP'}
                </Typography>
              </Box>
            </Box>
          }
          secondary={
            <Box component="div" sx={{ mt: 1 }}>
              {getDeviceStatusChips(device)}
              <Typography component="div" variant="caption" color="warning.dark" sx={{ fontWeight: 600 }}>
                No Parent Connection
              </Typography>
            </Box>
          }
          primaryTypographyProps={{ component: 'div' }}
          secondaryTypographyProps={{ component: 'div' }}
        />
      </ListItem>
    );

    if (rootDevices.length === 0 && orphanedDevices.length === 0) {
      return (
        <Paper>
          <List>
            <ListItem>
              <ListItemText primary="No devices found" />
            </ListItem>
          </List>
        </Paper>
      );
    }

    return (
      <Paper>
        <List>
          {rootDevices.map(device => renderDeviceTree(device, 0, new Set()))}
          
          {/* Orphaned Devices Section */}
          {orphanedDevices.length > 0 && (
            <>
              <ListItem sx={{ mt: 2, mb: 1 }}>
                <ListItemText
                  primary={
                    <Typography variant="h6" sx={{ 
                      fontSize: '1rem', 
                      fontWeight: 600, 
                      color: 'warning.dark',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      ⚠️ Orphaned Devices ({orphanedDevices.length})
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      Devices not connected to any parent network
                    </Typography>
                  }
                />
              </ListItem>
              {orphanedDevices.map(device => renderOrphanedDevice(device))}
            </>
          )}
        </List>
      </Paper>
    );
  };

  const handleDeleteClick = (device) => {
    if (!canWriteBackhaul) return;
    setDeviceToDelete(device);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deviceToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.deleteDevice(deviceToDelete.serial_number);
      // Remove the deleted device from the list
      onDevicesUpdate(devices.filter(d => d.serial_number !== deviceToDelete.serial_number));
      setDeleteDialogOpen(false);
      setDeviceToDelete(null);
    } catch (error) {
      console.error('Error deleting device:', error);
      console.error('Failed to delete device');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeviceToDelete(null);
  };

  if (!devicesLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!devices || devices.length === 0) {
    return <Alert severity="info">No devices connected</Alert>;
  }
  return (
    <Box>
      {renderFilters()}
      {viewMode !== 'map' && renderPaginationInfo()}
      {viewMode === 'map' ? (
        <DeviceMapView
          devices={getFilteredDevices()}
          allDevices={devices}
          onRefresh={onRefresh}
          highlightedGroupSerials={highlightedGroupSerials}
          canWriteBackhaul={auth.canWriteBackhaul()}
        />
      ) :
       viewMode === 'list' ? renderListView() : 
       viewMode === 'hierarchical' ? renderHierarchicalView() :
        <RadioView devices={getPaginatedDevices()} isLoading={!devicesLoaded} onDeviceClick={navigateToDevice} />}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Device</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete device {deviceToDelete?.serial_number}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DeviceList; 